import {
  EditorView,
  extension,
  getNodeType,
  PlainExtension,
} from '@remirror/core';
import { NodeSelection, Plugin } from '@remirror/pm/state';
import { command, CommandFunction, isElementDomNode } from 'remirror';
import { __serializeForClipboard } from '@remirror/pm/view';

@extension({
  defaultOptions: {},
  staticKeys: [],
})
export class MouseTrackerExtension extends PlainExtension<{
  isDisabled: boolean;
  isMobile: boolean;
  hasBorder: boolean;
}> {
  get name() {
    return 'dragndrop';
  }

  private menuOpened = false;

  @command()
  setMenuOpened(opened: boolean): CommandFunction {
    return (props) => {
      this.menuOpened = opened;

      return true;
    };
  }

  @command()
  handleNewItemButton(selectionPos: number, nodeSize: number): CommandFunction {
    return (props) => {
      const { tr, dispatch, state } = props;

      this.store.commands.focus(selectionPos);
      const isList = this.store.active.listItem();

      if (isList) {
        this.store
          .chain(tr)
          .focus(selectionPos + nodeSize - 2)
          .tr();

        const listItemType = getNodeType('listItem', state.schema);
        const types = [{ type: listItemType, attrs: [] }];

        tr.split(selectionPos + nodeSize - 2, 2, types);
        this.store.chain(tr).liftListItemOutOfList().tr();
      } else {
        this.store
          .chain(tr)
          .insertHtml('<p></p>', {
            selection: selectionPos + nodeSize - 1,
          })
          .tr();
      }
      this.store
        .chain(tr)
        .focus(selectionPos + nodeSize)
        .tr();

      dispatch?.(tr);
      return true;
    };
  }

  createExternalPlugins() {
    let timeout: NodeJS.Timeout | null = null;

    const scheduleIndicatorHide = (delay: number) => {
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(() => {
        const dragHandles = document.getElementsByClassName('drag-handle');
        if (dragHandles.length === 1) {
          const dragHandleElement = dragHandles.item(0);
          if (dragHandleElement instanceof HTMLDivElement) {
            dragHandleElement.style.opacity = '0';
          }
        }
      }, delay);
    };

    const handlerPlugin = new Plugin({
      props: {
        handleDOMEvents: {
          mouseover: (view, event) => {
            if (
              !view.editable ||
              !(event instanceof MouseEvent) ||
              this.menuOpened
            ) {
              return false;
            }

            let pos = view.posAtDOM(event.target as Node, 0) - 1;

            if (pos < 0) {
              if (this.options.isMobile) {
                pos = 0;
              } else {
                return false;
              }
            }

            let node = view.nodeDOM(pos);
            let resolvedPos = view.state.doc.resolve(pos);
            while (resolvedPos.depth > 1 && pos > 0) {
              pos--;
              node = view.nodeDOM(pos);
              resolvedPos = view.state.doc.resolve(pos);
            }

            if (!node || !isElementDomNode(node)) {
              return false;
            }

            const boundingRect = node.getBoundingClientRect();
            const topPos = boundingRect.top - 2;

            const dragHandles = document.getElementsByClassName('drag-handle');
            if (dragHandles.length === 1) {
              const dragHandleElement = dragHandles.item(0);
              if (dragHandleElement instanceof HTMLDivElement) {
                if (dragHandleElement.style.top !== `${topPos}px`) {
                  dragHandleElement.style.left = `${
                    view.dom.getBoundingClientRect().x - 24
                  }px`;
                  dragHandleElement.style.top = `${topPos}px`;
                  dragHandleElement.style.opacity = '1';
                }
                if (this.options.isDisabled) {
                  dragHandleElement.style.display = 'none';
                } else {
                  dragHandleElement.style.display = 'inline-flex';
                }
              }
            }

            const toolbars =
              document.getElementsByClassName('addBlocksToolbar');

            if (toolbars.length === 1) {
              const toolbar = toolbars.item(0);
              if (toolbar instanceof HTMLDivElement) {
                if (this.options.isMobile) {
                  const emptyParagraph =
                    view.nodeDOM(pos)?.textContent?.length === 0;
                  if (emptyParagraph) {
                    toolbar.style.display = 'block';
                  } else {
                    toolbar.style.display = 'none';
                  }
                }
                if (toolbar.style.top !== `${topPos}px`) {
                  toolbar.style.left = `${
                    view.dom.getBoundingClientRect().x -
                    (this.options.isMobile
                      ? this.options.hasBorder
                        ? 22
                        : 11
                      : this.options.isDisabled
                      ? 24
                      : 48)
                  }px`;
                  toolbar.style.top = `${topPos}px`;
                  toolbar.style.opacity = '1';
                  toolbar.setAttribute('data-pos', '' + pos);
                }
              }
            }

            return true;
          },
          dragend: (view, event) => {
            scheduleIndicatorHide(20);
            return false;
          },
          drop: (view, event) => {
            scheduleIndicatorHide(20);
            return false;
          },
          dragleave: (view, event) => {
            if (event.target === view.dom) {
              scheduleIndicatorHide(20);
            }
            return false;
          },
        },
      },
      view: (view) => {
        const parentView = view.dom.parentElement?.parentElement?.parentElement;
        let dragHandleElement: HTMLDivElement | null = null;

        if (parentView) {
          dragHandleElement = createDraghandle(view, parentView);
        }

        return {
          destroy: () => {
            dragHandleElement && dragHandleElement.remove();
            dragHandleElement = null;
          },
          update: (view) => {
            if (!dragHandleElement) {
              const parentView =
                view.dom.parentElement?.parentElement?.parentElement;

              if (parentView) {
                dragHandleElement = createDraghandle(view, parentView);
              }
            }
          },
        };
      },
    });

    return [handlerPlugin];
  }
}

declare global {
  namespace Remirror {
    interface AllExtensions {
      mousetracker: MouseTrackerExtension;
    }
  }
}

const createDraghandle = (view: EditorView, parentView: HTMLElement) => {
  let dragHandleElement: HTMLDivElement | null = null;
  dragHandleElement = document.createElement('div');
  dragHandleElement.draggable = true;
  dragHandleElement.classList.add('drag-handle');
  dragHandleElement.innerHTML = `
    <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 10 10' class='drag-handle-icon'>
      <path fill='currentColor' d='M3,2 C2.44771525,2 2,1.55228475 2,1 C2,0.44771525 2.44771525,0 3,0 C3.55228475,0 4,0.44771525 4,1 C4,1.55228475 3.55228475,2 3,2 Z M3,6 C2.44771525,6 2,5.55228475 2,5 C2,4.44771525 2.44771525,4 3,4 C3.55228475,4 4,4.44771525 4,5 C4,5.55228475 3.55228475,6 3,6 Z M3,10 C2.44771525,10 2,9.55228475 2,9 C2,8.44771525 2.44771525,8 3,8 C3.55228475,8 4,8.44771525 4,9 C4,9.55228475 3.55228475,10 3,10 Z M7,2 C6.44771525,2 6,1.55228475 6,1 C6,0.44771525 6.44771525,0 7,0 C7.55228475,0 8,0.44771525 8,1 C8,1.55228475 7.55228475,2 7,2 Z M7,6 C6.44771525,6 6,5.55228475 6,5 C6,4.44771525 6.44771525,4 7,4 C7.55228475,4 8,4.44771525 8,5 C8,5.55228475 7.55228475,6 7,6 Z M7,10 C6.44771525,10 6,9.55228475 6,9 C6,8.44771525 6.44771525,8 7,8 C7.55228475,8 8,8.44771525 8,9 C8,9.55228475 7.55228475,10 7,10 Z'></path>
    </svg>
  `;
  dragHandleElement.style.opacity = '0';
  dragHandleElement.addEventListener('dragstart', (e) => {
    const coords = {
      left: view.dom.getBoundingClientRect().x,
      top: e.clientY,
    };

    const pos = view.posAtCoords(coords)?.pos || 0;
    const selection = NodeSelection.create(view.state.doc, pos);
    const transaction = view.state.tr.setSelection(selection);
    view.dispatch(transaction);

    const slice = view.state.selection.content();
    const { dom, text } = __serializeForClipboard(view, slice);

    e.dataTransfer?.clearData();
    e.dataTransfer?.setData('text/html', dom.innerHTML);
    e.dataTransfer?.setData('text/plain', text);

    const draggedElement = view.nodeDOM(pos);
    if (draggedElement && isElementDomNode(draggedElement))
      e.dataTransfer?.setDragImage(draggedElement, 0, 0);

    view.dom.classList.add('dragging');

    view.dragging = { slice, move: true };
  });
  parentView.appendChild(dragHandleElement);

  return dragHandleElement;
};
